// HCCTelemetry.js — coder interaction telemetry
//
// Captures, on the whole HCCReviewPage screen (not scoped per-panel):
//   - session_start / session_end   — mount → unmount of HCCReviewPage
//   - page_enter / page_exit        — activePdfPage changes, with duration_ms
//   - click                         — every click anywhere in the four panels
//   - mouse samples                 — throttled, batched, sent separately
//
// Usage in HCCReviewPage.jsx:
//   const { telemetryRootRef } = useTelemetry({
//     chartId, sessionId, encounterId: selectedEnc?.encounter_id, activePdfPage,
//   });
//   ...
//   <Layout ref={telemetryRootRef}> ... four panels ... </Layout>
//
// PHI SAFETY NOTE: click payloads capture target tag/id/class and an optional
// data-telemetry-label attribute — never raw innerText. Chart evidence, OCR
// text, and code descriptions render as free text throughout these panels;
// capturing arbitrary innerText on click would leak chart content into the
// telemetry DB. If you want a click to carry a human-readable label (e.g.
// "ACCEPT button"), add data-telemetry-label="accept_finding" to that element
// — see the instrumentation notes for a suggested list.

import { useEffect, useRef, useCallback } from 'react';
import AgentConfigService from '../../services/AgentConfigService';

const TELEMETRY_ENABLED = process.env.REACT_APP_TELEMETRY_ENABLED !== 'false'; // on by default; set 'false' to disable

const FLUSH_INTERVAL_MS   = 5000;   // periodic flush cadence
const MOUSE_SAMPLE_MS     = 150;    // min gap between recorded mouse samples
const MAX_EVENTS_BUFFER   = 500;    // safety cap — matches backend MAX_EVENTS_PER_BATCH
const MAX_SAMPLES_BUFFER  = 2000;   // safety cap — matches backend MAX_SAMPLES_PER_BATCH

const genId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `vs-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const useTelemetry = ({ chartId, sessionId, encounterId, activePdfPage }) => {
  const telemetryRootRef = useRef(null);

  // ── Stable identity for this HCCReviewPage visit ───────────────────────────
  const viewSessionIdRef = useRef(null);
  if (!viewSessionIdRef.current) viewSessionIdRef.current = genId();

  // ── Latest-value refs — listeners are attached once, so closures must read
  // current context via refs rather than capture stale state ─────────────────
  const chartIdRef       = useRef(chartId);
  const encounterIdRef   = useRef(encounterId);
  const activePageRef    = useRef(activePdfPage);
  useEffect(() => { chartIdRef.current     = chartId;       }, [chartId]);
  useEffect(() => { encounterIdRef.current = encounterId;   }, [encounterId]);
  useEffect(() => { activePageRef.current  = activePdfPage; }, [activePdfPage]);

  // ── Buffers ──────────────────────────────────────────────────────────────
  const eventsBufferRef = useRef([]);
  const mouseBufferRef  = useRef([]);
  const lastMouseTsRef  = useRef(0);

  // ── Page-enter bookkeeping ───────────────────────────────────────────────
  const currentPageRef    = useRef(null);
  const pageEnteredAtRef  = useRef(null);
  const sessionStartedAtRef = useRef(null);

  // ── Base URL ─────────────────────────────────────────────────────────────
  const getBaseUrl = useCallback(
    () => AgentConfigService.getTelemetryServiceUrl(),
    []
  );

  // ── Push a discrete event into the buffer ───────────────────────────────
  const pushEvent = useCallback((eventType, opts = {}) => {
    if (!TELEMETRY_ENABLED) return;
    const buf = eventsBufferRef.current;
    if (buf.length >= MAX_EVENTS_BUFFER) return; // drop rather than grow unbounded
    buf.push({
      view_session_id: viewSessionIdRef.current,
      chart_id:        chartIdRef.current || null,
      encounter_id:    encounterIdRef.current || null,
      event_type:      eventType,
      page_ref:        opts.page_ref !== undefined
                          ? opts.page_ref
                          : (activePageRef.current != null ? String(activePageRef.current) : null),
      client_ts:       new Date().toISOString(),
      payload:         opts.payload || null,
    });
  }, []);

  // ── Flush events + mouse buffers to the backend ─────────────────────────
  const flush = useCallback(async (useKeepalive = false) => {
    if (!TELEMETRY_ENABLED) return;

    const events  = eventsBufferRef.current;
    const samples = mouseBufferRef.current;
    eventsBufferRef.current = [];
    mouseBufferRef.current  = [];

    if (events.length === 0 && samples.length === 0) return;

    try {
      const base = await getBaseUrl();
      const headers = {
        'Authorization': `Bearer ${sessionId}`,
        'Content-Type':  'application/json',
      };

      if (events.length > 0) {
        fetch(`${base}/telemetry/events`, {
          method: 'POST', headers, credentials: 'include', keepalive: useKeepalive,
          body: JSON.stringify({ events }),
        }).catch(() => {}); // best-effort — telemetry must never surface errors to the coder
      }

      if (samples.length > 0) {
        fetch(`${base}/telemetry/mouse-batch`, {
          method: 'POST', headers, credentials: 'include', keepalive: useKeepalive,
          body: JSON.stringify({
            view_session_id: viewSessionIdRef.current,
            chart_id:        chartIdRef.current || null,
            encounter_id:    encounterIdRef.current || null,
            batch_start_ts:  new Date(samples[0].t).toISOString(),
            batch_end_ts:    new Date(samples[samples.length - 1].t).toISOString(),
            samples,
          }),
        }).catch(() => {});
      }
    } catch {
      // Swallow — telemetry is fire-and-forget by design (see chat history:
      // never let this block or surface to the review workflow)
    }
  }, [sessionId, getBaseUrl]);

  // ── Session start / end (mount / unmount) ───────────────────────────────
  useEffect(() => {
    if (!TELEMETRY_ENABLED || !chartId) return;
    sessionStartedAtRef.current = Date.now();
    pushEvent('session_start');

    return () => {
      // Close out whatever page was open when the coder navigated away
      if (currentPageRef.current != null) {
        pushEvent('page_exit', {
          page_ref: String(currentPageRef.current),
          payload:  { duration_ms: Date.now() - (pageEnteredAtRef.current || Date.now()) },
        });
      }
      pushEvent('session_end', {
        page_ref: null,
        payload:  { total_duration_ms: Date.now() - (sessionStartedAtRef.current || Date.now()) },
      });
      // fetch(..., {keepalive:true}) rather than navigator.sendBeacon — beacon
      // can't carry the Authorization header our verify_session dependency
      // requires, and keepalive fetch is now broadly supported for this exact
      // "final request during unload" case.
      flush(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId]);

  // ── Page enter / exit — driven by activePdfPage changes ─────────────────
  useEffect(() => {
    if (!TELEMETRY_ENABLED) return;
    const prevPage = currentPageRef.current;
    const now = Date.now();

    if (prevPage != null && prevPage !== activePdfPage) {
      pushEvent('page_exit', {
        page_ref: String(prevPage),
        payload:  { duration_ms: now - (pageEnteredAtRef.current || now) },
      });
    }
    if (activePdfPage != null && activePdfPage !== prevPage) {
      pushEvent('page_enter', { page_ref: String(activePdfPage) });
      pageEnteredAtRef.current = now;
    }
    currentPageRef.current = activePdfPage ?? null;
  }, [activePdfPage, pushEvent]);

  // ── Click capture — document-level listener ──────────────────────────────
  // Attached to `document`, not the root container. antd Modals (Decision
  // Note, Add Code, Audit results, Finalize, etc.) render via React Portal
  // to document.body by default — their buttons are DOM siblings of the
  // Layout root, not descendants, so a listener on telemetryRootRef alone
  // would silently miss every click inside any modal. Root-relative
  // coordinates are kept when the click is inside the tracked subtree;
  // clicks in a portaled modal fall back to viewport-relative (0,0 origin)
  // since there's no meaningful "page container" for them.
  useEffect(() => {
    if (!TELEMETRY_ENABLED) return;

    const handleClick = (e) => {
      const target = e.target;
      const node = telemetryRootRef.current;
      const inRoot = node && node.contains(target);
      const rect = inRoot ? node.getBoundingClientRect() : { left: 0, top: 0 };
      pushEvent('click', {
        payload: {
          x:           Math.round(e.clientX - rect.left),
          y:           Math.round(e.clientY - rect.top),
          target_tag:  target.tagName || null,
          target_id:   target.id || null,
          // Opt-in only — never raw innerText (chart/OCR content risk). See
          // instrumentation notes for where to add data-telemetry-label.
          target_label:      target.closest('[data-telemetry-label]')?.dataset.telemetryLabel || null,
          // finding_id is an opaque DB identifier, not diagnostic content —
          // safe to carry as its own field the same way chart_id/encounter_id
          // already are. Deliberately NOT reading a "code" attribute here —
          // the actual ICD-10 code is diagnosis-bearing; see instrumentation
          // notes before adding one.
          target_finding_id: target.closest('[data-telemetry-finding-id]')?.dataset.telemetryFindingId || null,
        },
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [pushEvent]);

  // ── Mouse move capture — throttled + batched, document-level ────────────
  // Same portal reasoning as the click listener above — a mousemove inside
  // an open antd Modal wouldn't reach a listener scoped to the root node.
  useEffect(() => {
    if (!TELEMETRY_ENABLED) return;

    const handleMouseMove = (e) => {
      const now = Date.now();
      if (now - lastMouseTsRef.current < MOUSE_SAMPLE_MS) return;
      lastMouseTsRef.current = now;

      const buf = mouseBufferRef.current;
      if (buf.length >= MAX_SAMPLES_BUFFER) return;

      // Prefer coordinates relative to the specific rendered PDF page under
      // the cursor (page-relative — matches ev-hl-overlay positioning and is
      // what a future heatmap-per-page view needs). Falls back to
      // root-container-relative when inside the tracked subtree but not
      // over a PDF page, and to viewport-relative when the cursor is over a
      // portaled modal (outside the root subtree entirely).
      const pageEl = e.target.closest?.('[data-page-number]');
      const node = telemetryRootRef.current;
      let x, y, pageRef;
      if (pageEl) {
        const r = pageEl.getBoundingClientRect();
        x = Math.round(e.clientX - r.left);
        y = Math.round(e.clientY - r.top);
        pageRef = activePageRef.current != null ? String(activePageRef.current) : null;
      } else {
        const inRoot = node && node.contains(e.target);
        const r = inRoot ? node.getBoundingClientRect() : { left: 0, top: 0 };
        x = Math.round(e.clientX - r.left);
        y = Math.round(e.clientY - r.top);
        pageRef = null;
      }

      buf.push({ x, y, t: now, page_ref: pageRef });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // ── Periodic flush ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!TELEMETRY_ENABLED) return;
    const interval = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [flush]);

  // ── Flush on tab hide (covers backgrounding without a full unmount) ─────
  useEffect(() => {
    if (!TELEMETRY_ENABLED) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [flush]);

  return { telemetryRootRef };
};